import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  ModalBody,
} from "reactstrap";
import classnames from "classnames";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import { Formik, Field, ErrorMessage, useFormik } from "formik";
import * as Yup from "yup";
//redux
import { useSelector, useDispatch } from "react-redux";

import {
  changePassword as onChangePassword,
  getUserProfile as onGetUserProfile,
  updateUserProfile as onUpdateUserProfile,
} from "../../../../slices/thunks";
import { updatePreferredCurrency } from "../../../../slices/user/thunk";
import { selectPreferredCurrency } from "../../../../slices/user/selectors";

//import images
import progileBg from "../../../../assets/images/profile-bg.jpg";
import avatar1 from "../../../../assets/images/users/user-dummy-img.jpg";
import { useProfile } from "../../../../Components/Hooks/UserHooks";

const Settings = () => {
  const commonPasswords = [
    "123456",
    "password",
    "123456789",
    "12345678",
    "12345",
    "1234567",
    "qwerty",
    "abc123",
    "password1",
    "111111",
  ];
  const { userProfile, loading } = useProfile();
  const [name, setName] = useState("Loading...");
  const [role, setRole] = useState("Loading...");
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [user, setUser] = useState(null);
  const dispatch = useDispatch();

  const preferredCurrency = useSelector(selectPreferredCurrency);

  const handleCurrencyChange = (currency) => {
    dispatch(updatePreferredCurrency(currency));
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      username: user?.username || "",
      email: user?.email || "",
      is_superuser: user?.is_superuser || false,
      date_joined: user?.date_joined || null,
    },
    validationSchema: Yup.object({
      first_name: Yup.string()
        .required("Please Enter First Name")
        .min(3, "First Name must be at least 3 characters long"),
      last_name: Yup.string()
        .required("Please Enter Last Name")
        .min(3, "Last Name must be at least 3 characters long"),
      username: Yup.string()
        .required("Please Enter Username")
        .min(3, "Username must be at least 3 characters long"),
      email: Yup.string()
        .required("Please Enter Email")
        .email("Please Enter a Valid Email"),
    }),
    onSubmit: async (values) => {
      const sanitizedValues = { ...values };
      Object.keys(sanitizedValues).forEach((key) => {
        if (sanitizedValues[key] === "") {
          sanitizedValues[key] = null;
        }
      });

      const updateUser = {
        id: userProfile?.user?.id,
        first_name: sanitizedValues.first_name,
        last_name: sanitizedValues.last_name,
        username: sanitizedValues.username,
        email: sanitizedValues.email,
        is_superuser: user?.is_superuser || false,
        date_joined: user?.date_joined || null,
      };

      try {
        await dispatch(onUpdateUserProfile(updateUser)).unwrap();

        dispatch(onGetUserProfile())
          .unwrap()
          .then((updatedUser) => {
            setUser(updatedUser);
          })
          .catch((error) => {
            console.error("Failed to fetch updated user:", error);
          });
      } catch (error) {
        console.error("Failed to update user:", error);
      }

      validation.resetForm();
    },
  });

  useEffect(() => {
    if (!userProfile?.user?.id) return;

    dispatch(onGetUserProfile())
      .unwrap()
      .then((fetchedUser) => {
        if (!fetchedUser) return;

        setUser(fetchedUser);

        const fullName = `${fetchedUser.first_name || ""} ${
          fetchedUser.last_name || ""
        }`.trim();
        setName(fullName || fetchedUser.username || "User");

        const userRole = fetchedUser.groups?.[0] || "User";
        setRole(userRole);

        const totalFields = Object.keys(fetchedUser).length;
        const completedFields = Object.values(fetchedUser).filter(
          (value) => value !== null && value !== undefined && value !== ""
        ).length;
        setProfileCompletion(Math.round((completedFields / totalFields) * 100));
      })
      .catch((error) => {
        console.error("Failed to fetch user:", error);
      });
  }, [dispatch, userProfile?.user?.id]);

  const tabChange = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const passwordValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      old_password: "",
      new_password1: "",
      new_password2: "",
    },
    validationSchema: Yup.object({
      old_password: Yup.string().required("Please Enter Current Password"),
      new_password1: Yup.string()
        .required("Please Enter New Password")
        .min(8, "Password must be at least 8 characters long")
        .matches(
          /^(?=.*[0-9])(?=.*[A-Za-z]).*$/,
          "Password must contain both letters and numbers"
        )
        .test(
          "attribute-similarity",
          "Password is too similar to your username or email",
          function (value) {
            const { username, email } = this.parent;
            return (
              value &&
              !(
                (username && value.includes(username)) ||
                (email && value.includes(email.split("@")[0]))
              )
            );
          }
        )
        .notOneOf(commonPasswords, "Password is too common")
        .test(
          "numeric-password",
          "Password must not be entirely numeric",
          (value) => value && !/^\d+$/.test(value)
        ),
      new_password2: Yup.string()
        .required("Please Confirm New Password")
        .oneOf([Yup.ref("new_password1"), null], "Passwords must match"),
    }),
    onSubmit: (values) => {
      const { old_password, new_password1, new_password2 } = values;

      const passwordData = {
        old_password,
        new_password1,
        new_password2,
      };

      dispatch(onChangePassword(passwordData));

      passwordValidation.resetForm();
    },
  });

  const defaultdate = () => {
    let d = new Date(),
      months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
    let h = d.getHours() % 12 || 12;
    let ampm = d.getHours() < 12 ? "AM" : "PM";
    return (
      d.getDate() +
      " " +
      months[d.getMonth()] +
      ", " +
      d.getFullYear() +
      ", " +
      h +
      ":" +
      d.getMinutes() +
      " " +
      ampm
    ).toString();
  };

  const [date, setDate] = useState(defaultdate());

  const dateformate = (e) => {
    const dateString = e.toString().split(" ");

    let time = dateString[4];
    let H = +time.substr(0, 2);
    let h = H % 12 || 12;
    h = h <= 9 ? (h = "0" + h) : h;
    let ampm = H < 12 ? "AM" : "PM";
    time = h + time.substr(2, 3) + " " + ampm;

    const date = dateString[2] + " " + dateString[1] + ", " + dateString[3];
    const orderDate = (date + ", " + time).toString();
    setDate(orderDate);
  };

  const handleValidDate = (date) => {
    return moment(new Date(date)).format("DD MMM YYYY"); // Use "YYYY" for 4-digit year
  };

  const handleValidTime = (time) => {
    const time1 = new Date(time);
    const getHour = time1.getUTCHours();
    const getMin = time1.getUTCMinutes();
    const getTime = `${getHour}:${getMin}`;
    var meridiem = "";
    if (getHour >= 12) {
      meridiem = "PM";
    } else {
      meridiem = "AM";
    }
    const updateTime =
      moment(getTime, "hh:mm").format("hh:mm") + " " + meridiem;
    return updateTime;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="position-relative mx-n4 mt-n4">
            <div className="profile-wid-bg profile-setting-img">
              <img src={progileBg} className="profile-wid-img" alt="" />
              <div className="overlay-content">
                <div className="text-end p-3">
                  <div className="p-0 ms-auto rounded-circle profile-photo-edit">
                    <Input
                      id="profile-foreground-img-file-input"
                      type="file"
                      className="profile-foreground-img-file-input"
                    />
                    <Label
                      htmlFor="profile-foreground-img-file-input"
                      className="profile-photo-edit btn btn-light"
                    >
                      <i className="ri-image-edit-line align-bottom me-1"></i>{" "}
                      Change Cover
                    </Label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Row>
            <Col xxl={3}>
              <Card className="mt-n5">
                <CardBody className="p-4">
                  <div className="text-center">
                    <div className="profile-user position-relative d-inline-block mx-auto  mb-4">
                      <img
                        src={avatar1}
                        className="rounded-circle avatar-xl img-thumbnail user-profile-image"
                        alt="user-profile"
                      />
                      <div className="avatar-xs p-0 rounded-circle profile-photo-edit">
                        <Input
                          id="profile-img-file-input"
                          type="file"
                          className="profile-img-file-input"
                        />
                        <Label
                          htmlFor="profile-img-file-input"
                          className="profile-photo-edit avatar-xs"
                        >
                          <span className="avatar-title rounded-circle bg-light text-body">
                            <i className="ri-camera-fill"></i>
                          </span>
                        </Label>
                      </div>
                    </div>
                    <h5 className="fs-16 mb-1">{name}</h5>
                    <p className="text-muted mb-0">{role}</p>
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <div className="d-flex align-items-center mb-5">
                    <div className="flex-grow-1">
                      <h5 className="card-title mb-0">Complete Your Profile</h5>
                    </div>
                    <div className="flex-shrink-0">
                      <Link
                        to="#"
                        className="badge bg-light text-primary fs-12"
                        onClick={() => tabChange("1")} // Set the active tab to "1"
                      >
                        <i className="ri-edit-box-line align-bottom me-1"></i>{" "}
                        Edit
                      </Link>
                    </div>
                  </div>

                  <div className="progress animated-progress custom-progress progress-label">
                    <div
                      className="progress-bar bg-danger"
                      role="progressbar"
                      style={{ width: `${profileCompletion}%` }}
                      aria-valuenow={profileCompletion}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    >
                      <div className="label">{profileCompletion}%</div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xxl={9}>
              <Card className="mt-xxl-n5">
                <CardHeader>
                  <Nav
                    className="nav-tabs-custom rounded card-header-tabs border-bottom-0"
                    role="tablist"
                  >
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "1" })}
                        onClick={() => {
                          tabChange("1");
                        }}
                      >
                        <i className="fas fa-home"></i>
                        Personal Details
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        to="#"
                        className={classnames({ active: activeTab === "2" })}
                        onClick={() => {
                          tabChange("2");
                        }}
                        type="button"
                      >
                        <i className="far fa-user"></i>
                        Change Password
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        to="#"
                        className={classnames({ active: activeTab === "5" })}
                        onClick={() => tabChange("5")}
                        type="button"
                      >
                        <i className="ri-settings-3-line"></i>
                        Preferences
                      </NavLink>
                    </NavItem>
                    {/* <NavItem >
                                            <NavLink to="#"
                                                className={classnames({ active: activeTab === "3" })}
                                                onClick={() => {
                                                    tabChange("3");
                                                }}
                                                type="button">
                                                <i className="far fa-envelope"></i>
                                                Experience
                                            </NavLink>
                                        </NavItem>
                                        <NavItem>
                                            <NavLink to="#"
                                                className={classnames({ active: activeTab === "4" })}
                                                onClick={() => {
                                                    tabChange("4");
                                                }}
                                                type="button">
                                                <i className="far fa-envelope"></i>
                                                Privacy Policy
                                            </NavLink>
                                        </NavItem> */}
                  </Nav>
                </CardHeader>
                <CardBody className="p-4">
                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="1">
                      <Form onSubmit={validation.handleSubmit}>
                        <Row>
                          <Col md={6}>
                            <Label for="first_name">First Name</Label>
                            <Input
                              type="text"
                              id="first_name"
                              name="first_name"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.first_name}
                              invalid={
                                validation.touched.first_name &&
                                !!validation.errors.first_name
                              }
                            />
                            {validation.touched.first_name &&
                              validation.errors.first_name && (
                                <div className="invalid-feedback">
                                  {validation.errors.first_name}
                                </div>
                              )}
                          </Col>
                          <Col md={6}>
                            <Label for="last_name">Last Name</Label>
                            <Input
                              type="text"
                              id="last_name"
                              name="last_name"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.last_name}
                              invalid={
                                validation.touched.last_name &&
                                !!validation.errors.last_name
                              }
                            />
                            {validation.touched.last_name &&
                              validation.errors.last_name && (
                                <div className="invalid-feedback">
                                  {validation.errors.last_name}
                                </div>
                              )}
                          </Col>
                          <Col md={6}>
                            <Label for="username">Username</Label>
                            <Input
                              type="text"
                              id="username"
                              name="username"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.username}
                              invalid={
                                validation.touched.username &&
                                !!validation.errors.username
                              }
                            />
                            {validation.touched.username &&
                              validation.errors.username && (
                                <div className="invalid-feedback">
                                  {validation.errors.username}
                                </div>
                              )}
                          </Col>
                          <Col md={6}>
                            <Label for="email">Email</Label>
                            <Input
                              type="email"
                              id="email"
                              name="email"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.email}
                              invalid={
                                validation.touched.email &&
                                !!validation.errors.email
                              }
                            />
                            {validation.touched.email &&
                              validation.errors.email && (
                                <div className="invalid-feedback">
                                  {validation.errors.email}
                                </div>
                              )}
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label htmlFor="groups" className="form-label">
                                Groups
                              </Label>
                              <Input
                                type="text"
                                id="groups"
                                className="form-control"
                                value={user?.groups || "No groups assigned"} // Safely handle undefined groups
                                readOnly
                                disabled
                              />
                            </div>
                          </Col>

                          <Col lg={6}>
                            <div className="mb-3">
                              <Label htmlFor="id" className="form-label">
                                User ID
                              </Label>
                              <Input
                                type="text"
                                id="id"
                                className="form-control"
                                value={user?.id || "No"}
                                readOnly
                                disabled
                              />
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor="is_superuser"
                                className="form-label"
                              >
                                Super User
                              </Label>
                              <Input
                                type="text"
                                id="is_superuser"
                                className="form-control"
                                value={
                                  user?.is_superuser
                                    ? "Yes, you have superuser access."
                                    : "No, you don't have superuser access."
                                }
                                readOnly
                                disabled
                              />
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor="date_joined"
                                className="form-label"
                              >
                                Date Joined
                              </Label>
                              <Input
                                type="text"
                                id="date_joined"
                                className="form-control"
                                value={`${handleValidDate(
                                  user?.date_joined
                                )}, ${handleValidTime(user?.date_joined)}`}
                                readOnly
                              />
                            </div>
                          </Col>
                        </Row>
                        <button type="submit" className="btn btn-primary mt-3">
                          Submit
                        </button>
                      </Form>
                    </TabPane>

                    <TabPane tabId="2">
                      <Form onSubmit={passwordValidation.handleSubmit}>
                        <Row className="g-2">
                          <Col lg={4}>
                            <div>
                              <Label
                                htmlFor="oldpasswordInput"
                                className="form-label"
                              >
                                Old Password*
                              </Label>
                              <Input
                                type="password"
                                className="form-control"
                                id="oldpasswordInput"
                                name="old_password"
                                placeholder="Enter current password"
                                onChange={passwordValidation.handleChange}
                                onBlur={passwordValidation.handleBlur}
                                value={passwordValidation.values.old_password}
                                invalid={
                                  passwordValidation.touched.old_password &&
                                  !!passwordValidation.errors.old_password
                                }
                              />
                              {passwordValidation.touched.old_password &&
                                passwordValidation.errors.old_password && (
                                  <div className="invalid-feedback">
                                    {passwordValidation.errors.old_password}
                                  </div>
                                )}
                            </div>
                          </Col>

                          <Col lg={4}>
                            <div>
                              <Label
                                htmlFor="newpasswordInput"
                                className="form-label"
                              >
                                New Password*
                              </Label>
                              <Input
                                type="password"
                                className="form-control"
                                id="newpasswordInput"
                                name="new_password1"
                                placeholder="Enter new password"
                                onChange={passwordValidation.handleChange}
                                onBlur={passwordValidation.handleBlur}
                                value={passwordValidation.values.new_password1}
                                invalid={
                                  passwordValidation.touched.new_password1 &&
                                  !!passwordValidation.errors.new_password1
                                }
                              />
                              {passwordValidation.touched.new_password1 &&
                                passwordValidation.errors.new_password1 && (
                                  <div className="invalid-feedback">
                                    {passwordValidation.errors.new_password1}
                                  </div>
                                )}
                            </div>
                          </Col>

                          <Col lg={4}>
                            <div>
                              <Label
                                htmlFor="confirmpasswordInput"
                                className="form-label"
                              >
                                Confirm Password*
                              </Label>
                              <Input
                                type="password"
                                className="form-control"
                                id="confirmpasswordInput"
                                name="new_password2"
                                placeholder="Confirm password"
                                onChange={passwordValidation.handleChange}
                                onBlur={passwordValidation.handleBlur}
                                value={passwordValidation.values.new_password2}
                                invalid={
                                  passwordValidation.touched.new_password2 &&
                                  !!passwordValidation.errors.new_password2
                                }
                              />
                              {passwordValidation.touched.new_password2 &&
                                passwordValidation.errors.new_password2 && (
                                  <div className="invalid-feedback">
                                    {passwordValidation.errors.new_password2}
                                  </div>
                                )}
                            </div>
                          </Col>

                          {/* <Col lg={12}>
                                                    <div className="mb-3">
                                                        <Link to="#" className="link-primary text-decoration-underline">Forgot Password?</Link>
                                                    </div>
                                                </Col> */}

                          <Col lg={12}>
                            <div className="text-end">
                              <button type="submit" className="btn btn-success">
                                Change Password
                              </button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </TabPane>

                    <TabPane tabId="5">
                      <Row className="g-3">
                        <Col lg={12}>
                          <h6 className="fw-semibold text-muted text-uppercase fs-11 mb-3">Display Currency</h6>
                          <p className="text-muted fs-13 mb-4">
                            Choose the currency used to display amounts across the app. All values will be converted using the live BNR exchange rate.
                          </p>
                          <div className="d-flex gap-3">
                            {["RON", "EUR"].map((cur) => (
                              <div
                                key={cur}
                                onClick={() => handleCurrencyChange(cur)}
                                style={{ cursor: "pointer", minWidth: 160 }}
                                className={`card border-2 mb-0 ${preferredCurrency === cur ? "border-primary" : "border-light"}`}
                              >
                                <div className="card-body d-flex align-items-center gap-3 py-3">
                                  <div
                                    className={`rounded-circle d-flex align-items-center justify-content-center fw-bold fs-14`}
                                    style={{
                                      width: 40, height: 40, flexShrink: 0,
                                      background: preferredCurrency === cur ? "#002045" : "#f1f5f9",
                                      color: preferredCurrency === cur ? "#fff" : "#64748b",
                                    }}
                                  >
                                    {cur === "RON" ? "lei" : "€"}
                                  </div>
                                  <div>
                                    <div className="fw-semibold">{cur}</div>
                                    <div className="text-muted fs-12">
                                      {cur === "RON" ? "Romanian Leu" : "Euro"}
                                    </div>
                                  </div>
                                  {preferredCurrency === cur && (
                                    <i className="ri-checkbox-circle-fill text-primary ms-auto fs-18" />
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </Col>
                      </Row>
                    </TabPane>

                    <TabPane tabId="3">
                      <form>
                        <div id="newlink">
                          <div id="1">
                            <Row>
                              <Col lg={12}>
                                <div className="mb-3">
                                  <Label
                                    htmlFor="jobTitle"
                                    className="form-label"
                                  >
                                    Job Title
                                  </Label>
                                  <Input
                                    type="text"
                                    className="form-control"
                                    id="jobTitle"
                                    placeholder="Job title"
                                    defaultValue="Lead Designer / Developer"
                                  />
                                </div>
                              </Col>

                              <Col lg={6}>
                                <div className="mb-3">
                                  <Label
                                    htmlFor="companyName"
                                    className="form-label"
                                  >
                                    Company Name
                                  </Label>
                                  <Input
                                    type="text"
                                    className="form-control"
                                    id="companyName"
                                    placeholder="Company name"
                                    defaultValue="Themesbrand"
                                  />
                                </div>
                              </Col>

                              <Col lg={6}>
                                <div className="mb-3">
                                  <label
                                    htmlFor="experienceYear"
                                    className="form-label"
                                  >
                                    Experience Years
                                  </label>
                                  <Row>
                                    <Col lg={5}>
                                      <select
                                        className="form-control"
                                        data-choices
                                        data-choices-search-false
                                        name="experienceYear"
                                        id="experienceYear"
                                      >
                                        <option defaultValue="">
                                          Select years
                                        </option>
                                        <option value="Choice 1">2001</option>
                                        <option value="Choice 2">2002</option>
                                        <option value="Choice 3">2003</option>
                                        <option value="Choice 4">2004</option>
                                        <option value="Choice 5">2005</option>
                                        <option value="Choice 6">2006</option>
                                        <option value="Choice 7">2007</option>
                                        <option value="Choice 8">2008</option>
                                        <option value="Choice 9">2009</option>
                                        <option value="Choice 10">2010</option>
                                        <option value="Choice 11">2011</option>
                                        <option value="Choice 12">2012</option>
                                        <option value="Choice 13">2013</option>
                                        <option value="Choice 14">2014</option>
                                        <option value="Choice 15">2015</option>
                                        <option value="Choice 16">2016</option>
                                        <option value="Choice 17">2017</option>
                                        <option value="Choice 18">2018</option>
                                        <option value="Choice 19">2019</option>
                                        <option value="Choice 20">2020</option>
                                        <option value="Choice 21">2021</option>
                                        <option value="Choice 22">2022</option>
                                      </select>
                                    </Col>

                                    <div className="col-auto align-self-center">
                                      to
                                    </div>

                                    <Col lg={5}>
                                      <select
                                        className="form-control"
                                        data-choices
                                        data-choices-search-false
                                        name="choices-single-default2"
                                      >
                                        <option defaultValue="">
                                          Select years
                                        </option>
                                        <option value="Choice 1">2001</option>
                                        <option value="Choice 2">2002</option>
                                        <option value="Choice 3">2003</option>
                                        <option value="Choice 4">2004</option>
                                        <option value="Choice 5">2005</option>
                                        <option value="Choice 6">2006</option>
                                        <option value="Choice 7">2007</option>
                                        <option value="Choice 8">2008</option>
                                        <option value="Choice 9">2009</option>
                                        <option value="Choice 10">2010</option>
                                        <option value="Choice 11">2011</option>
                                        <option value="Choice 12">2012</option>
                                        <option value="Choice 13">2013</option>
                                        <option value="Choice 14">2014</option>
                                        <option value="Choice 15">2015</option>
                                        <option value="Choice 16">2016</option>
                                        <option value="Choice 17">2017</option>
                                        <option value="Choice 18">2018</option>
                                        <option value="Choice 19">2019</option>
                                        <option value="Choice 20">2020</option>
                                        <option value="Choice 21">2021</option>
                                        <option value="Choice 22">2022</option>
                                      </select>
                                    </Col>
                                  </Row>
                                </div>
                              </Col>

                              <Col lg={12}>
                                <div className="mb-3">
                                  <Label
                                    htmlFor="jobDescription"
                                    className="form-label"
                                  >
                                    Job Description
                                  </Label>
                                  {/* <textarea className="form-control" id="jobDescription"
                                                                    defaultValue=""
                                                                        rows="3"
                                                                        placeholder="Enter description">You always want to make sure that your fonts work well together and try to limit the number of fonts you use to three or less. Experiment and play around with the fonts that you already have in the software you're working with reputable font websites. </textarea> */}
                                </div>
                              </Col>

                              <div className="hstack gap-2 justify-content-end">
                                <Link className="btn btn-success" to="#">
                                  Delete
                                </Link>
                              </div>
                            </Row>
                          </div>
                        </div>
                        <div id="newForm" style={{ display: "none" }}></div>

                        <Col lg={12}>
                          <div className="hstack gap-2">
                            <button type="submit" className="btn btn-success">
                              Update
                            </button>
                            <Link to="#" className="btn btn-primary">
                              Add New
                            </Link>
                          </div>
                        </Col>
                      </form>
                    </TabPane>

                    <TabPane tabId="4">
                      <div className="mb-4 pb-2">
                        <h5 className="card-title text-decoration-underline mb-3">
                          Security:
                        </h5>
                        <div className="d-flex flex-column flex-sm-row mb-4 mb-sm-0">
                          <div className="flex-grow-1">
                            <h6 className="fs-14 mb-1">
                              Two-factor Authentication
                            </h6>
                            <p className="text-muted">
                              Two-factor authentication is an enhanced security
                              meansur. Once enabled, you'll be required to give
                              two types of identification when you log into
                              Google Authentication and SMS are Supported.
                            </p>
                          </div>
                          <div className="flex-shrink-0 ms-sm-3">
                            <Link to="#" className="btn btn-sm btn-primary">
                              Enable Two-facor Authentication
                            </Link>
                          </div>
                        </div>
                        <div className="d-flex flex-column flex-sm-row mb-4 mb-sm-0 mt-2">
                          <div className="flex-grow-1">
                            <h6 className="fs-14 mb-1">
                              Secondary Verification
                            </h6>
                            <p className="text-muted">
                              The first factor is a password and the second
                              commonly includes a text with a code sent to your
                              smartphone, or biometrics using your fingerprint,
                              face, or retina.
                            </p>
                          </div>
                          <div className="flex-shrink-0 ms-sm-3">
                            <Link to="#" className="btn btn-sm btn-primary">
                              Set up secondary method
                            </Link>
                          </div>
                        </div>
                        <div className="d-flex flex-column flex-sm-row mb-4 mb-sm-0 mt-2">
                          <div className="flex-grow-1">
                            <h6 className="fs-14 mb-1">Backup Codes</h6>
                            <p className="text-muted mb-sm-0">
                              A backup code is automatically generated for you
                              when you turn on two-factor authentication through
                              your iOS or Android Twitter app. You can also
                              generate a backup code on twitter.com.
                            </p>
                          </div>
                          <div className="flex-shrink-0 ms-sm-3">
                            <Link to="#" className="btn btn-sm btn-primary">
                              Generate backup codes
                            </Link>
                          </div>
                        </div>
                      </div>
                      <div className="mb-3">
                        <h5 className="card-title text-decoration-underline mb-3">
                          Application Notifications:
                        </h5>
                        <ul className="list-unstyled mb-0">
                          <li className="d-flex">
                            <div className="flex-grow-1">
                              <label
                                htmlFor="directMessage"
                                className="form-check-label fs-14"
                              >
                                Direct messages
                              </label>
                              <p className="text-muted">
                                Messages from people you follow
                              </p>
                            </div>
                            <div className="flex-shrink-0">
                              <div className="form-check form-switch">
                                <Input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  id="directMessage"
                                  defaultChecked
                                />
                              </div>
                            </div>
                          </li>
                          <li className="d-flex mt-2">
                            <div className="flex-grow-1">
                              <Label
                                className="form-check-label fs-14"
                                htmlFor="desktopNotification"
                              >
                                Show desktop notifications
                              </Label>
                              <p className="text-muted">
                                Choose the option you want as your default
                                setting. Block a site: Next to "Not allowed to
                                send notifications," click Add.
                              </p>
                            </div>
                            <div className="flex-shrink-0">
                              <div className="form-check form-switch">
                                <Input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  id="desktopNotification"
                                  defaultChecked
                                />
                              </div>
                            </div>
                          </li>
                          <li className="d-flex mt-2">
                            <div className="flex-grow-1">
                              <Label
                                className="form-check-label fs-14"
                                htmlFor="emailNotification"
                              >
                                Show email notifications
                              </Label>
                              <p className="text-muted">
                                {" "}
                                Under Settings, choose Notifications. Under
                                Select an account, choose the account to enable
                                notifications for.{" "}
                              </p>
                            </div>
                            <div className="flex-shrink-0">
                              <div className="form-check form-switch">
                                <Input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  id="emailNotification"
                                />
                              </div>
                            </div>
                          </li>
                          <li className="d-flex mt-2">
                            <div className="flex-grow-1">
                              <Label
                                className="form-check-label fs-14"
                                htmlFor="chatNotification"
                              >
                                Show chat notifications
                              </Label>
                              <p className="text-muted">
                                To prevent duplicate mobile notifications from
                                the Gmail and Chat apps, in settings, turn off
                                Chat notifications.
                              </p>
                            </div>
                            <div className="flex-shrink-0">
                              <div className="form-check form-switch">
                                <Input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  id="chatNotification"
                                />
                              </div>
                            </div>
                          </li>
                          <li className="d-flex mt-2">
                            <div className="flex-grow-1">
                              <Label
                                className="form-check-label fs-14"
                                htmlFor="purchaesNotification"
                              >
                                Show purchase notifications
                              </Label>
                              <p className="text-muted">
                                Get real-time purchase alerts to protect
                                yourself from fraudulent charges.
                              </p>
                            </div>
                            <div className="flex-shrink-0">
                              <div className="form-check form-switch">
                                <Input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  id="purchaesNotification"
                                />
                              </div>
                            </div>
                          </li>
                        </ul>
                      </div>
                      <div>
                        <h5 className="card-title text-decoration-underline mb-3">
                          Delete This Account:
                        </h5>
                        <p className="text-muted">
                          Go to the Data & Privacy section of your profile
                          Account. Scroll to "Your data & privacy options."
                          Delete your Profile Account. Follow the instructions
                          to delete your account :
                        </p>
                        <div>
                          <Input
                            type="password"
                            className="form-control"
                            id="passwordInput"
                            placeholder="Enter your password"
                            defaultValue="make@321654987"
                            style={{ maxWidth: "265px" }}
                          />
                        </div>
                        <div className="hstack gap-2 mt-3">
                          <Link to="#" className="btn btn-soft-danger">
                            Close & Delete This Account
                          </Link>
                          <Link to="#" className="btn btn-light">
                            Cancel
                          </Link>
                        </div>
                      </div>
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Settings;
